import React from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const About = () => {
  const values = [
    {
      icon: '🏗️',
      title: 'Engineering First',
      description:
        'Clean architecture, secure defaults, and maintainable codebases, not throwaway prototypes dressed as products.',
    },
    {
      icon: '🤝',
      title: 'Partnership',
      description:
        'We work as an extension of your team: clear updates, honest trade-offs, and decisions you can stand behind.',
    },
    {
      icon: '📱',
      title: 'Multi-Platform Thinking',
      description:
        'Web, iOS, and Android are planned together so data, identity, and experience stay consistent across devices.',
    },
    {
      icon: '📊',
      title: 'Measurable Outcomes',
      description:
        'Features and campaigns are tied to business goals, adoption, efficiency, revenue, or reach.',
    },
  ];

  const focusAreas = [
    {
      title: 'Product engineering',
      text: 'Custom .NET web applications, APIs, admin portals, and mobile clients that integrate with the systems you already run.',
    },
    {
      title: 'Platform & cloud',
      text: 'Azure-hosted environments, CI/CD, identity, and operational monitoring so releases are repeatable and recoverable.',
    },
    {
      title: 'Digital growth',
      text: 'SEO, paid acquisition, and email programs that introduce your product to the right audience after launch.',
    },
  ];

  return (
    <main className="about-page">
      <section className="about-hero page-hero section">
        <div className="container">
          <p className="eyebrow">About Softnova Solutions</p>
          <h1 className="section-title">Software development and digital growth</h1>
          <p className="section-subtitle">
            Softnova builds .NET and PHP web applications, iOS and Android apps, and the
            SEO and campaign work that helps those products get found.
          </p>
        </div>
      </section>

      <section className="about-story section">
        <div className="container">
          <div className="story-content">
            <h2 className="story-title">Who We Are</h2>
            <div className="story-text">
              <p>
                Softnova Solutions is a development studio for companies that need a working
                product, not only a marketing site. We take web, mobile, and digital growth
                as one brief so architecture, apps, and acquisition stay aligned.
              </p>
              <p>
                On the web, we specialize in the Microsoft ecosystem, C#, ASP.NET Core,
                Blazor, Entity Framework, SQL Server, and Azure. That stack is trusted by
                enterprises worldwide for security, performance, and long-term support. We
                use it to deliver customer portals, internal operations platforms, APIs, and
                industry-specific business applications.
              </p>
              <p>
                On mobile, we deliver native iOS apps in Swift and SwiftUI, native Android
                apps in Kotlin and Jetpack Compose, and cross-platform products with .NET
                MAUI, Flutter, or React Native when a shared codebase is the right call.
                Every app is planned for store guidelines, push notifications, analytics,
                and ongoing release cycles.
              </p>
              <p>
                Alongside engineering, our digital practice covers SEO, search and social
                advertising, email marketing, and analytics. Many clients come to us for an
                application and stay because launch day is not the finish line, it is when
                growth work begins.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-focus section">
        <div className="container">
          <h2 className="section-title">Where We Focus</h2>
          <p className="section-subtitle">
            Three capabilities, one accountable team.
          </p>
          <div className="focus-grid">
            {focusAreas.map((area) => (
              <div key={area.title} className="focus-card">
                <h3>{area.title}</h3>
                <p>{area.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-values section">
        <div className="container">
          <h2 className="section-title">How We Operate</h2>
          <div className="values-grid">
            {values.map((value) => (
              <div key={value.title} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-cta section">
        <div className="container">
          <div className="about-cta-content">
            <h2 className="about-cta-title">Let&apos;s Talk About Your Roadmap</h2>
            <p className="about-cta-subtitle">
              Whether you are starting a new platform or modernizing an existing one, we
              would like to understand your goals.
            </p>
            <div className="about-cta-buttons">
              <Link to="/contact" className="btn btn-light">
                Contact Us
              </Link>
              <Link to="/mission" className="btn btn-secondary about-cta-secondary">
                Read Our Mission
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
